// skipcq: JS-W1028
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useBeerStyles from './useBeerStyles';
import useBeerStyleTypes from './useBeerStyleTypes';
import { useWebSearchForBeer } from './useWebSearchForBeer';
import { useBeerImport } from './useBeerImport';
import FindBeerDialog from '../pages/brewery/findBeer/FindBeerDialog';
import { buildCatalogStyleMapping } from '../utils/beerStyle/catalogStyleMapping';

function validateSourceUrl(url) {
  if (!url || typeof url !== 'string') {
    return null;
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const parsedUrl = new URL(trimmed);
    const protocol = parsedUrl.protocol.toLowerCase();
    if (protocol !== 'http:' && protocol !== 'https:') {
      return null;
    }
    return parsedUrl.toString();
  } catch {
    return null;
  }
}

/**
 * useBreweryFindBeerFlow
 * Shared Find Beer search/scrape/import flow for brewery manager surfaces.
 */
function useBreweryFindBeerFlow({
  parentBreweryId,
  breweryName,
  breweryCity,
  breweryState,
  onBeersImported,
}) {
  const [findBeerDialogOpen, setFindBeerDialogOpen] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [showImportSummary, setShowImportSummary] = useState(false);
  const [selectedBeerIndices, setSelectedBeerIndices] = useState([]);
  const [styleTypeByName, setStyleTypeByName] = useState({});
  const handledScrapeUrlRef = useRef(null);

  const { beerStyles, loading: beerStylesLoading } = useBeerStyles();
  const { styleTypes, loading: styleTypesLoading } = useBeerStyleTypes();
  const catalogLoading = Boolean(beerStylesLoading || styleTypesLoading);
  const styleMapping = useMemo(
    () => buildCatalogStyleMapping(beerStyles),
    [beerStyles],
  );

  const beerSearch = useWebSearchForBeer(breweryName, breweryCity, breweryState);
  const { dialogOpenRef, reset: resetBeerSearch, handleSearchResultSelect } = beerSearch;
  const { importBeers, loading: importLoading, error: importError } = useBeerImport(
    parentBreweryId,
    beerSearch.scrapingResult,
    beerSearch.selectedUrl,
    beerSearch.beerUrl,
    styleMapping,
    styleTypeByName,
    beerSearch.dialogOpenRef,
    selectedBeerIndices,
  );

  useEffect(() => {
    dialogOpenRef.current = findBeerDialogOpen;
  }, [findBeerDialogOpen, dialogOpenRef]);

  const handleAssignStyleType = useCallback((event) => {
    const styleName = event.target.name;
    const styleType = event.target.value;
    if (!styleName || !styleType) {
      return;
    }
    setStyleTypeByName((current) => ({ ...current, [styleName]: styleType }));
  }, []);

  const handleCloseFindBeerDialog = useCallback(() => {
    setFindBeerDialogOpen(false);
    beerSearch.reset();
    setImportResults(null);
    setShowImportSummary(false);
    setSelectedBeerIndices([]);
    setStyleTypeByName({});
    handledScrapeUrlRef.current = null;
  }, [beerSearch]);

  const handleFindBeer = useCallback(async () => {
    setFindBeerDialogOpen(true);
    await beerSearch.handleFindBeer();
  }, [beerSearch]);

  const openFindBeerWithUrl = useCallback(
    (url) => {
      const validatedUrl = validateSourceUrl(url);
      if (!validatedUrl) {
        console.warn('Invalid URL provided for scraping:', url);
        return false;
      }

      if (handledScrapeUrlRef.current === validatedUrl) {
        return true;
      }
      handledScrapeUrlRef.current = validatedUrl;

      setFindBeerDialogOpen(true);
      resetBeerSearch();
      handleSearchResultSelect(validatedUrl);
      return true;
    },
    [resetBeerSearch, handleSearchResultSelect],
  );

  const handleBeerSelectionChange = useCallback((indices) => {
    setSelectedBeerIndices(indices);
  }, []);

  const handleImportBeers = useCallback(async () => {
    const result = await importBeers();
    if (result) {
      setImportResults(result);
      setShowImportSummary(true);
      if (onBeersImported) {
        await onBeersImported();
      }
    }
  }, [importBeers, onBeersImported]);

  const findBeerDialog = (
    <FindBeerDialog
      open={findBeerDialogOpen}
      onClose={handleCloseFindBeerDialog}
      showImportSummary={showImportSummary}
      searchLoading={beerSearch.searchLoading}
      searchError={beerSearch.searchError}
      searchResults={beerSearch.searchResults}
      scrapingLoading={beerSearch.scrapingLoading}
      scrapingUrl={beerSearch.scrapingUrl}
      scrapingError={beerSearch.scrapingError}
      scrapingResult={beerSearch.scrapingResult}
      selectedUrl={beerSearch.selectedUrl}
      importLoading={importLoading}
      catalogLoading={catalogLoading}
      importError={importError}
      importResults={importResults}
      styleMapping={styleMapping}
      styleTypes={styleTypes}
      styleTypeByName={styleTypeByName}
      selectedBeerIndices={selectedBeerIndices}
      onAssignStyleType={handleAssignStyleType}
      onSelectClick={beerSearch.handleSelectClick}
      onLinkClick={beerSearch.handleLinkClick}
      onImportBeers={handleImportBeers}
      onSelectionChange={handleBeerSelectionChange}
    />
  );

  return {
    handleFindBeer,
    openFindBeerWithUrl,
    findBeerDialog,
  };
}

export default useBreweryFindBeerFlow;
